import { motion } from "framer-motion";
import { ArrowDown, ArrowUpRight, Croissant } from "lucide-react";
import { HERO_IMAGE, HERO_POLAROID } from "@/data";
import { scrollToId } from "@/lib/scroll";
import { MaskLine, FadeIn } from "@/components/Reveal";

export const Hero = () => (
  <section className="relative min-h-screen flex items-center overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 lg:px-12 w-full pt-28 pb-20 lg:pt-32 lg:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-10 items-center">
      <div className="lg:col-span-7">
        <FadeIn delay={0.1} inView={false}>
          <p className="eyebrow mb-6 lg:mb-8">Boulangerie · Pâtisserie · Artisan Franchise</p>
        </FadeIn>
        <h1 className="font-display font-medium leading-[0.95] tracking-tight text-[clamp(3.8rem,9.5vw,8.5rem)]">
          <MaskLine delay={0.15} inView={false}>Butter.</MaskLine>
          <MaskLine delay={0.28} inView={false}>Flour.</MaskLine>
          <MaskLine delay={0.41} inView={false} innerClassName="italic text-[#C85A32]">Patience.</MaskLine>
        </h1>
        <FadeIn delay={0.55} inView={false}>
          <p className="mt-7 max-w-md text-base lg:text-lg leading-relaxed text-[#1C130E]/75">
            French Pie is an artisan French bakery. Laminated doughs rest for 72 hours, butter goes in by the
            kilo, and every outlet bakes from the same recipe book — every single morning.
          </p>
        </FadeIn>
        <FadeIn delay={0.7} inView={false}>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#menu"
              data-testid="hero-explore-menu-btn"
              onClick={(e) => {
                e.preventDefault();
                scrollToId("#menu");
              }}
              className="group inline-flex items-center gap-2 rounded-full bg-[#C85A32] text-[#FDFBF7] px-7 py-3.5 text-sm font-semibold hover:bg-[#B04B26] transition-colors"
            >
              Explore the menu
              <ArrowDown size={16} className="transition-transform duration-300 group-hover:translate-y-0.5" />
            </a>
            <a
              href="#manifesto"
              data-testid="hero-manifesto-link"
              onClick={(e) => {
                e.preventDefault();
                scrollToId("#manifesto");
              }}
              className="group inline-flex items-center gap-1.5 rounded-full border border-[rgba(28,19,14,0.25)] px-7 py-3.5 text-sm font-semibold hover:border-[#C85A32] hover:text-[#C85A32] transition-colors"
            >
              Our manifesto
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:rotate-45" />
            </a>
          </div>
        </FadeIn>
      </div>
      <div className="lg:col-span-5 relative">
        <motion.div
          className="relative mx-auto w-[78%] max-w-sm lg:w-full lg:max-w-none"
          initial={{ clipPath: "inset(100% 0 0 0)", scale: 1.02 }}
          animate={{ clipPath: "inset(0% 0 0 0)", scale: 1 }}
          transition={{ duration: 1.2, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="overflow-hidden rounded-[2rem] border border-[rgba(28,19,14,0.08)] shadow-[0_40px_80px_-30px_rgba(28,19,14,0.45)]">
            <img
              alt="The French Pie shop front at night — lit signage above sage-green doors"
              className="w-full aspect-[16/11] object-cover"
              src={HERO_IMAGE}
            />
          </div>
          <motion.div
            className="absolute -left-8 sm:-left-12 top-8 w-24 h-24 sm:w-28 sm:h-28 grid place-items-center"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            <svg viewBox="0 0 120 120" className="absolute inset-0 spin-slow">
              <defs>
                <path id="badge-circle" d="M60,60 m-46,0 a46,46 0 1,1 92,0 a46,46 0 1,1 -92,0" />
              </defs>
              <text className="fill-[#1C130E]" style={{ fontSize: "10.5px", letterSpacing: "0.22em" }}>
                <textPath href="#badge-circle">FRENCH PIE · PÂTISSERIE · MAISON ·</textPath>
              </text>
            </svg>
            <span className="grid place-items-center w-11 h-11 rounded-full bg-[#C85A32] text-[#FDFBF7]">
              <Croissant size={18} strokeWidth={1.5} />
            </span>
          </motion.div>
          <motion.div
            className="float-y z-10 absolute -bottom-8 -left-6 sm:-left-14 w-36 sm:w-44 bg-white p-2.5 pb-4 shadow-[0_24px_50px_-20px_rgba(28,19,14,0.4)] rounded-sm"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15, ease: [0.22, 1, 0.36, 1] }}
          >
            <img alt="Tart served with cream" className="w-full aspect-square object-cover" src={HERO_POLAROID} />
            <p className="font-mono-accent text-[9px] tracking-[0.14em] uppercase text-[#1C130E]/60 mt-2 text-center whitespace-nowrap">
              Tarte maison
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  </section>
);
